import { ReactNode } from "react";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

// Compatibility shim: mirrors the legacy WFP-UI `Modal` prop API but renders our
// shadcn Dialog. Lets the many existing Modal consumers migrate by swapping
// only the import. Footer = secondary + primary buttons (or a custom
// `modalFooter` render override; pass `() => <></>` to hide it).
interface ModalProps {
  open?: boolean;
  modalHeading?: ReactNode;
  modalLabel?: ReactNode;
  onRequestClose?: () => void;
  onRequestSubmit?: () => void;
  primaryButtonText?: ReactNode;
  primaryButtonDisabled?: boolean;
  secondaryButtonText?: ReactNode;
  onSecondarySubmit?: () => void;
  modalFooter?: () => ReactNode;
  className?: string;
  children?: ReactNode;
}

const Modal = (props: ModalProps) => {
  const {
    open,
    modalHeading,
    modalLabel,
    onRequestClose,
    onRequestSubmit,
    primaryButtonText,
    primaryButtonDisabled,
    secondaryButtonText,
    onSecondarySubmit,
    modalFooter,
    className,
    children,
  } = props;

  const defaultFooter =
    primaryButtonText || secondaryButtonText ? (
      <DialogFooter>
        {!!secondaryButtonText && (
          <Button
            variant="secondary"
            onClick={() => (onSecondarySubmit ? onSecondarySubmit() : onRequestClose?.())}
          >
            {secondaryButtonText}
          </Button>
        )}
        {!!primaryButtonText && (
          <Button variant="default" disabled={primaryButtonDisabled} onClick={() => onRequestSubmit?.()}>
            {primaryButtonText}
          </Button>
        )}
      </DialogFooter>
    ) : null;

  return (
    <Dialog
      open={!!open}
      onOpenChange={(o) => {
        if (!o) onRequestClose?.();
      }}
    >
      <DialogContent
        className={cn("flex max-h-[85vh] flex-col sm:max-w-[560px]", className)}
        aria-describedby={undefined}
      >
        <DialogHeader className="shrink-0">
          {!!modalLabel && (
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{modalLabel}</p>
          )}
          {/* DialogTitle is required by Radix for a11y; render empty when no heading. */}
          <DialogTitle className={modalHeading ? undefined : "sr-only"}>{modalHeading ?? "Dialog"}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">{children}</div>
        {modalFooter ? <div className="shrink-0">{modalFooter()}</div> : defaultFooter}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
